import React, { useState } from 'react';
import { MediaModal } from '../../atoms/media/media-modal';
import { MediaSkeleton } from '../../atoms/media/media-skeleton';
import { MediaErrorSkeleton } from '../../atoms/media/media-error-skeleton';

interface ImageMediaProps {
  url: string;
  isLoading?: boolean;
  alt?: string;
  className?: string;
  footer?: React.ReactNode;
}

export const ImageMedia: React.FC<ImageMediaProps> = ({
  url,
  isLoading = false,
  alt = 'image',
  className = '',
  footer = null,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isLandscape, setIsLandscape] = useState(false);

  const handleReady = (event: React.SyntheticEvent<HTMLImageElement>) => {
    const img = event.currentTarget;
    const aspectRatio = img.naturalWidth / img.naturalHeight;
    setIsLandscape(aspectRatio > 1);
    setIsReady(true);
    setHasError(false);
  };

  const handleError = () => {
    setIsReady(false);
    setHasError(true);
  };

  const getDynamicContainerClasses = (): string => {
    const baseClasses = 'flex relative w-full h-auto rounded-lg';
    const sizeClasses = isLandscape
      ? 'max-w-[27rem] max-h-[14rem]'
      : 'max-w-[14rem] max-h-[27rem]';
    return `${baseClasses} ${sizeClasses} ${className}`;
  };

  const containerClasses = getDynamicContainerClasses();
  const imageLoading = (isLoading || !isReady) && !hasError;

  return (
    <>
      <div className="flex flex-col w-full gap-2 justify-start">
        <div className={containerClasses}>
          {imageLoading && <MediaSkeleton type="image" size="sm" />}
          {hasError && <MediaErrorSkeleton type="image" size="sm" />}
          <img
            src={url}
            alt={alt}
            onClick={() => !imageLoading && !hasError && setIsModalOpen(true)}
            onLoad={handleReady}
            onError={handleError}
            className={`w-full h-full rounded-lg cursor-pointer object-cover ${
              isReady ? 'block' : 'hidden'
            }`}
          />
        </div>

        {footer && footer}
      </div>

      <MediaModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <img
          src={url}
          alt={alt}
          onClick={() => setIsModalOpen(false)}
          onLoad={handleReady}
          onError={handleError}
          className={`max-h-[85vh] max-w-[85vw] w-auto h-auto rounded-lg cursor-pointer object-contain ${
            isReady ? 'block' : 'hidden'
          }`}
        />
      </MediaModal>
    </>
  );
};
